import { useState } from 'react'
import type { KeyboardEvent } from 'react'
import type { ForecastTeamSlice } from '@lms/shared-types'

export interface ForecastTeamCardProps {
  team: ForecastTeamSlice
}

type Severity = 'ochre' | 'clay'

function getSeverity(daysAtOrBelowMin: number): Severity {
  return daysAtOrBelowMin >= 3 ? 'clay' : 'ochre'
}

const SEVERITY_STYLES: Record<Severity, { bg: string; line: string; fg: string }> = {
  ochre: {
    bg: 'var(--warning-bg, #fef9ec)',
    line: 'var(--warning-line, #f0c14b)',
    fg: 'var(--warning-fg, #7a4f00)',
  },
  clay: {
    bg: 'var(--danger-bg, #fdf0ed)',
    line: 'var(--danger-line, #e07a5f)',
    fg: 'var(--danger-fg, #7c2d12)',
  },
}

/**
 * Accordion card showing a single team's coverage-breach forecast.
 *
 * Collapsed: team name + severity chip + pending count.
 * Expanded: breach date chips + top_pending employee list.
 *
 * Keyboard accessible: Enter / Space toggle open state.
 * ARIA: role="button", aria-expanded, tabIndex=0.
 */
export function ForecastTeamCard({ team }: ForecastTeamCardProps): JSX.Element {
  const [open, setOpen] = useState(false)

  const severity = getSeverity(team.days_at_or_below_min)
  const { bg, line, fg } = SEVERITY_STYLES[severity]

  function toggle(): void {
    setOpen((prev) => !prev)
  }

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      toggle()
    }
  }

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 8,
        overflow: 'hidden',
        background: 'var(--surface)',
      }}
    >
      {/* Header row */}
      <div
        data-testid="forecast-team-card-header"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={handleKeyDown}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          padding: '12px 16px',
          cursor: 'pointer',
          userSelect: 'none',
          background: open ? 'var(--surface-2)' : 'var(--surface)',
          borderBottom: open ? '1px solid var(--border)' : 'none',
          transition: 'background var(--motion-duration-fast, 80ms) ease',
        }}
      >
        {/* Left: severity chip + team name */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <span
            data-testid="forecast-severity-chip"
            data-severity={severity}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 28,
              height: 22,
              padding: '0 8px',
              borderRadius: 999,
              background: bg,
              border: `1px solid ${line}`,
              color: fg,
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.02em',
              flexShrink: 0,
            }}
          >
            {team.days_at_or_below_min}
          </span>

          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--text)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {team.team_name}
          </span>
        </div>

        {/* Right: pending count + chevron */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexShrink: 0,
          }}
        >
          <span
            style={{
              fontSize: 12,
              color: 'var(--text-3)',
              fontFamily: 'var(--font-mono)',
            }}
          >
            <span data-testid="forecast-team-pending-count">{team.pending_count}</span>
            {' '}pending
          </span>

          {/* Chevron */}
          <span
            aria-hidden="true"
            style={{
              display: 'inline-block',
              fontSize: 12,
              color: 'var(--text-3)',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform var(--motion-duration-fast, 80ms) ease',
            }}
          >
            ▾
          </span>
        </div>
      </div>

      {/* Expanded details */}
      {open ? (
        <div
          data-testid="forecast-team-card-details"
          style={{
            padding: '12px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          {/* Breach date chips */}
          {team.breach_dates.length > 0 ? (
            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'var(--text-3)',
                  marginBottom: 6,
                }}
              >
                Breach dates
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {team.breach_dates.map((date) => (
                  <span
                    key={date}
                    style={{
                      padding: '3px 8px',
                      borderRadius: 4,
                      background: bg,
                      border: `1px solid ${line}`,
                      color: fg,
                      fontFamily: 'var(--font-mono)',
                      fontSize: 11,
                    }}
                  >
                    {date}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {/* Top pending employees */}
          {team.top_pending.length > 0 ? (
            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'var(--text-3)',
                  marginBottom: 6,
                }}
              >
                Pending requests
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {team.top_pending.map((p) => (
                  <div
                    key={p.leave_id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '6px 10px',
                      borderRadius: 6,
                      background: 'var(--surface-2)',
                      border: '1px solid var(--french-gray-2)',
                      gap: 12,
                    }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{p.employee_name}</span>
                    <span
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 11,
                        color: 'var(--text-3)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {p.start_date} – {p.end_date} ({p.days}d)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* Empty detail state */}
          {team.breach_dates.length === 0 && team.top_pending.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
              No breach details available.
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
